import React, { useState, useEffect, useContext } from "react";
import { Add, Close } from "@mui/icons-material";
import {
  Button,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Stack,
} from "@mui/material";
import { SingleSelectType, MultiSelect } from "../../table/Filter";

import {
  getActivePaymentBrands,
  getCurrencyList,
  paymentHostAction,
} from "../../../services/payment";
import { showErrorMsg } from "../../../utils/toast";
import { PaymentHostContext } from "../PaymentHostSwitchView";
import { Log } from "../../../services/services";
import * as Yup from "yup";
import { useFormik } from "formik";
import { LoadingButton } from "@mui/lab";
import { styles } from "../../../utils/styles";
import FormDialog from "../../FormDialog";
import { GREY } from "../../../theme/palette";
import ConfigurationParams, {
  emptyConfigParam,
  validateConfigParams,
} from "../ConfigurationParams";

function CreateHostDialog() {
  const { dispatch } = useContext(PaymentHostContext);
  const [paymentBrands, setPaymentBrands] = useState([]);
  const [allowedCurrency, setAllowedCurrency] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    getActivePaymentBrands(setPaymentBrands, setLoaded);
    getCurrencyList(setAllowedCurrency, setLoaded);
  }, []);

  //Dialog Stuff
  const [open, setOpen] = useState(false);
  const handleClickOpen = () => {
    Log.info("Click button [New Payment Host] - Show popup Add New Payment Host");
    setOpen(true);
  };
  const handleClose = () => {
    resetForm();
    setFormFields([emptyConfigParam()]);
    setConfigErrors([]);
    setOpen(false);
  };

  //Config Params
  const [formFields, setFormFields] = useState([emptyConfigParam()]);
  const [configErrors, setConfigErrors] = useState([]);

  const handleFormChange = (event, index) => {
    let data = [...formFields];
    data[index][event.target.name] = event.target.value;
    setFormFields(data);

    let fieldErrors = [...configErrors];
    fieldErrors[index] = { ...fieldErrors[index], [event.target.name]: "" };
    setConfigErrors(fieldErrors);
  };

  const addFields = () => {
    setFormFields([...formFields, emptyConfigParam()]);
    setConfigErrors([...configErrors, {}]);
  };

  const removeFields = (index) => {
    let data = [...formFields];
    data.splice(index, 1);
    setFormFields(data);

    let fieldErrors = [...configErrors];
    fieldErrors.splice(index, 1);
    setConfigErrors(fieldErrors);
  };

  const {
    handleChange,
    handleSubmit,
    handleBlur,
    errors,
    values,
    touched,
    isSubmitting,
    resetForm,
    setFieldValue,
  } = useFormik({
    initialValues: {
      paymentHost: "",
      payment_brand: "",
      currency_list: []
    },
    validationSchema: Yup.object().shape({
      paymentHost: Yup.string().required("This field is required"),
      payment_brand: Yup.string().required("This field is required"),
      currency_list: Yup.array().min(1, "This field is required"),
    }),
    onSubmit: async (values) => {
      const { errors: paramErrors, hasError } = validateConfigParams(formFields);
      setConfigErrors(paramErrors);
      if (hasError) return;

      const paymentHost = values.paymentHost.trim();
      const data = {};
      formFields.forEach((field) => (data[field.param.trim()] = field.val));
  
      Log.info("Click button [Submit] - Add New Payment Host", {
        name: paymentHost,
        payment_brand: values.payment_brand,
        currency_list: values.currency_list,
        data: JSON.stringify(data),
      });
      await paymentHostAction("ADD", {
        name: paymentHost,
        payment_brand: values.payment_brand,
        currency_list: values.currency_list,
        data: JSON.stringify(data),
      },
      () => dispatch({ type: "FETCHING_PAYMENT_HOST" })
      );
      handleClose();
    },
  });

  // Formik only reaches onSubmit once the Yup schema passes, so the config
  // params are validated here as well to surface their errors on every Submit.
  const handleFormSubmit = (event) => {
    const { errors: paramErrors } = validateConfigParams(formFields);
    setConfigErrors(paramErrors);
    handleSubmit(event);
  };

  return (
    <div>
      <Button variant="contained" onClick={handleClickOpen} startIcon={<Add />} sx={{ ...styles.customButton }}>
        New Payment Host
      </Button>
      <FormDialog
        open={open}
        onClose={handleClose}
        title="Add New Payment Host"
        handleSubmit={handleFormSubmit}
        loading={isSubmitting}
        maxWidth="sm"
      >
        {loaded && (
          <SingleSelectType
            name="payment_brand"
            id="true"
            label="Payment Brand"
            options={paymentBrands.map((method) => method)}
            selected={values.payment_brand}
            setSelected={data => setFieldValue("payment_brand", data)}
            size="small"
            error={Boolean(touched.payment_brand && errors.payment_brand)}
            helperText={touched.payment_brand && errors.payment_brand}
          />
        )}

        <TextField
          name="paymentHost"
          label="Name of payment Host"
          fullWidth
          variant="standard"
          value={values.paymentHost}
          onChange={handleChange}
          onBlur={handleBlur}
          error={Boolean(touched.paymentHost && errors.paymentHost)}
          helperText={touched.paymentHost && errors.paymentHost}
          disabled={isSubmitting}
          sx={{ marginBottom: 4 }}
        />

        {loaded && (
          <>
            <Typography sx={{ pb: 0.5, color: GREY[600], fontSize: 12 }}>
              Currency
            </Typography>
            <MultiSelect
              name="currency_list"
              options={allowedCurrency.map((method) => ({
                value: method.id ? method.id : "",
                label: method.currency ? method.currency : "",
              }))}
              selectedValues={values.currency_list}
              setSelectedValues={data => setFieldValue("currency_list", data)}
              errorMsg={touched.currency_list && errors.currency_list}
            />
          </>
        )}
        <ConfigurationParams
          formFields={formFields}
          errors={configErrors}
          onChange={handleFormChange}
          onAdd={addFields}
          onRemove={removeFields}
          disabled={isSubmitting}
        />
      </FormDialog>
    </div>
  );
}

export default CreateHostDialog;
